import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { removeStorageFile } from '@/lib/storage';
import { canWrite, WRITE_PERMISSION_DENIED } from '@/services/permissions';
import { recordAudit } from '@/services/audit';

export interface Donation {
  id: string;
  periodName: string;
  salesBaseAmount: number;
  donationRate: number;
  donationGenerated: number;
  isDelivered: boolean;
  deliveredAt: string;
  receiptDocumentUrl: string;
  beneficiaryName: string;
  beneficiaryPhone: string;
  beneficiaryAddress: string;
  createdAt: string;
}

export interface DonationsResult {
  data: Donation[];
}

export async function fetchDonations(): Promise<DonationsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const { data, error } = await supabase.from('donations').select('*');

    if (error) {
      throw new Error(error.message);
    }

    const mapped: Donation[] = ((data ?? []) as Array<Record<string, any>>)
      .map((r) => ({
        id: r.id,
        periodName: r.period_name,
        beneficiaryName: r.beneficiary ?? '',
        beneficiaryPhone: r.beneficiary_phone ?? '',
        beneficiaryAddress: r.beneficiary_address ?? '',
        salesBaseAmount: Number(r.sales_base_amount),
        donationRate: Number(r.donation_rate),
        donationGenerated: Number(r.donation_generated),
        isDelivered: r.is_delivered ?? false,
        deliveredAt: r.delivered_at ?? '',
        receiptDocumentUrl: r.receipt_document_url ?? '',
        createdAt: r.created_at ?? '',
      }))
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));

    return { data: mapped };
  } catch (err) {
    console.error('[donations] connection failed:', err);
    throw err;
  }
}

export interface DonationInput {
  periodName: string;
  beneficiaryName: string;
  beneficiaryPhone: string;
  beneficiaryAddress: string;
  salesBaseAmount: number;
  donationRate: number;
}

// Crea un registro de donación por período calculando donation_generated.
export async function saveDonation(
  input: DonationInput,
): Promise<{ data: Donation | null; error: string | null }> {
  if (!(await canWrite('donations'))) {
    return { data: null, error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { data: null, error: 'Supabase no está configurado.' };
  }

  const donationGenerated = Number(input.salesBaseAmount) * (Number(input.donationRate) / 100);

  const payload = {
    period_name: input.periodName.trim(),
    beneficiary: input.beneficiaryName.trim() || null,
    beneficiary_phone: input.beneficiaryPhone.trim() || null,
    beneficiary_address: input.beneficiaryAddress.trim() || null,
    sales_base_amount: Number(input.salesBaseAmount),
    donation_rate: Number(input.donationRate),
    donation_generated: donationGenerated,
    is_delivered: false,
  };

  try {
    const { data, error } = await supabase.from('donations').insert(payload).select('*');
    if (error) return { data: null, error: error.message };
    const row = data?.[0] as Record<string, any> | undefined;
    if (!row) return { data: null, error: 'No se pudo leer la donación guardada.' };
    recordAudit({
      entityType: 'donations',
      entityId: row.id,
      action: 'CREATE',
      newValue: input.periodName,
    });
    return {
      data: {
        id: row.id,
        periodName: row.period_name,
        beneficiaryName: row.beneficiary ?? '',
        beneficiaryPhone: row.beneficiary_phone ?? '',
        beneficiaryAddress: row.beneficiary_address ?? '',
        salesBaseAmount: Number(row.sales_base_amount),
        donationRate: Number(row.donation_rate),
        donationGenerated: Number(row.donation_generated),
        isDelivered: row.is_delivered ?? false,
        deliveredAt: row.delivered_at ?? '',
        receiptDocumentUrl: row.receipt_document_url ?? '',
        createdAt: row.created_at ?? '',
      },
      error: null,
    };
  } catch (err) {
    console.warn('[donations] save failed:', err);
    return { data: null, error: 'Error de conexión al guardar la donación.' };
  }
}

// Marca una donación como entregada (o revierte).
export async function setDonationDelivered(
  id: string,
  isDelivered: boolean,
): Promise<{ error: string | null }> {
  if (!(await canWrite('donations'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const patch = isDelivered
    ? { is_delivered: true, delivered_at: new Date().toISOString() }
    : { is_delivered: false, delivered_at: null };
  try {
    const { error } = await supabase.from('donations').update(patch).eq('id', id);
    if (!error) {
      recordAudit({
        entityType: 'donations',
        entityId: id,
        action: 'UPDATE',
        fieldName: 'is_delivered',
        oldValue: String(!isDelivered),
        newValue: String(isDelivered),
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[donations] setDelivered failed:', err);
    return { error: 'Error de conexión al actualizar la donación.' };
  }
}

export interface DonationUpdateInput {
  id: string;
  periodName: string;
  beneficiaryName: string;
  beneficiaryPhone: string;
  beneficiaryAddress: string;
  salesBaseAmount: number;
  donationRate: number;
  receiptUrl: string;
  isDelivered: boolean;
  originalDeliveredAt: string;
}

// Actualiza una donación existente: datos del período, estado de entrega y
// recibo/documento de soporte.
export async function updateDonation(
  input: DonationUpdateInput,
): Promise<{ error: string | null }> {
  if (!(await canWrite('donations'))) {
    return { error: WRITE_PERMISSION_DENIED };
  }
  if (!isSupabaseConfigured || !supabase) {
    return { error: 'Supabase no está configurado.' };
  }

  const donationGenerated = Number(input.salesBaseAmount) * (Number(input.donationRate) / 100);

  // Si la donación ya estaba entregada, preserva su fecha original. Solo se
  // asigna "ahora" cuando se marca como entregada por primera vez.
  const deliveredAt = input.isDelivered
    ? input.originalDeliveredAt || new Date().toISOString()
    : null;

  const payload = {
    period_name: input.periodName.trim(),
    beneficiary: input.beneficiaryName.trim() || null,
    beneficiary_phone: input.beneficiaryPhone.trim() || null,
    beneficiary_address: input.beneficiaryAddress.trim() || null,
    sales_base_amount: Number(input.salesBaseAmount),
    donation_rate: Number(input.donationRate),
    donation_generated: donationGenerated,
    receipt_document_url: input.receiptUrl.trim() || null,
    is_delivered: input.isDelivered,
    delivered_at: deliveredAt,
  };

  try {
    const { error } = await supabase.from('donations').update(payload).eq('id', input.id);
    if (!error) {
      recordAudit({
        entityType: 'donations',
        entityId: input.id,
        action: 'UPDATE',
        fieldName: 'receipt_document_url',
        newValue: input.receiptUrl,
      });
    }
    return { error: error?.message ?? null };
  } catch (err) {
    console.warn('[donations] update failed:', err);
    return { error: 'Error de conexión al actualizar la donación.' };
  }
}

// Sube el recibo/comprobante de una donación al bucket `receipts` y devuelve
// la URL pública lista para guardar en `receipt_document_url`.
export async function uploadDonationReceipt(
  file: File,
): Promise<{ url: string | null; error: string | null }> {
  if (!isSupabaseConfigured || !supabase) {
    return { url: null, error: 'Supabase no está configurado. Conéctalo para subir archivos.' };
  }

  const safeBase =
    file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40) || 'comprobante';

  const ext = file.name.split('.').pop()?.toLowerCase().replace(/[^a-z0-9]/g, '') || 'pdf';
  const path = `${Date.now()}-${safeBase}.${ext}`;

  try {
    const { error } = await supabase.storage
      .from('receipts')
      .upload(path, file, { cacheControl: '3600', upsert: false });

    if (error) return { url: null, error: error.message };

    const { data } = supabase.storage.from('receipts').getPublicUrl(path);
    return { url: data.publicUrl, error: null };
  } catch (err) {
    console.warn('[donations] upload receipt failed:', err);
    return { url: null, error: 'Error de conexión al subir el comprobante.' };
  }
}

// Borra el recibo de una donación del bucket `receipts` a partir de su URL.
export async function deleteDonationReceipt(url: string): Promise<{ error: string | null }> {
  return removeStorageFile('receipts', url);
}

// --- Historial de donaciones (montos donados manualmente con fecha) ---

export interface DonationEntry {
  id: string;
  donationId: string;
  amount: number;
  donatedAt: string;
  notes: string;
  createdAt: string;
}

export async function fetchDonationEntries(donationId: string): Promise<DonationEntry[]> {
  if (!isSupabaseConfigured || !supabase) return [];
  const { data, error } = await supabase
    .from('donation_entries')
    .select('*')
    .eq('donation_id', donationId)
    .order('donated_at', { ascending: false, nullsFirst: false });
  if (error) {
    console.warn('[donations] fetchDonationEntries error', error.message);
    return [];
  }
  return ((data ?? []) as Array<Record<string, any>>).map((e) => ({
    id: e.id,
    donationId: e.donation_id,
    amount: Number(e.amount),
    donatedAt: e.donated_at ?? '',
    notes: e.notes ?? '',
    createdAt: e.created_at ?? '',
  }));
}

export async function addDonationEntry(input: {
  donationId: string;
  amount: number;
  donatedAt: string;
  notes: string;
}): Promise<{ error: string | null }> {
  if (!(await canWrite('donations'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('donation_entries').insert({
    donation_id: input.donationId,
    amount: Number(input.amount),
    donated_at: input.donatedAt || null,
    notes: input.notes.trim() || null,
  });
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'donations',
    entityId: input.donationId,
    action: 'UPDATE',
    fieldName: 'entry',
    newValue: `$${input.amount}`,
  });
  return { error: null };
}

export async function deleteDonationEntry(
  id: string,
  donationId: string,
): Promise<{ error: string | null }> {
  if (!(await canWrite('donations'))) return { error: WRITE_PERMISSION_DENIED };
  if (!isSupabaseConfigured || !supabase) return { error: 'Supabase no está configurado.' };

  const { error } = await supabase.from('donation_entries').delete().eq('id', id);
  if (error) return { error: error.message };

  recordAudit({
    entityType: 'donations',
    entityId: donationId,
    action: 'UPDATE',
    fieldName: 'entry',
    oldValue: 'deleted',
    newValue: '',
  });
  return { error: null };
}

// --- Reporte mensual: historial completo de valores donados con entidad ---

export interface DonationReportEntry {
  id: string;
  entityName: string;
  amount: number;
  donatedAt: string;
  notes: string;
}

export async function fetchDonationReportEntries(): Promise<DonationReportEntry[]> {
  if (!isSupabaseConfigured || !supabase) return [];

  const [entriesRes, donationsRes] = await Promise.all([
    supabase
      .from('donation_entries')
      .select('*')
      .order('donated_at', { ascending: false, nullsFirst: false }),
    supabase.from('donations').select('id, beneficiary, period_name'),
  ]);

  if (entriesRes.error) {
    console.warn('[donations] fetchDonationReportEntries error', entriesRes.error.message);
    return [];
  }

  const donationMap = new Map(
    ((donationsRes.data ?? []) as Array<Record<string, any>>).map((d) => [d.id, d]),
  );

  return ((entriesRes.data ?? []) as Array<Record<string, any>>).map((e) => {
    const donation = donationMap.get(e.donation_id);
    const entityName = donation?.beneficiary || donation?.period_name || 'Sin entidad';
    return {
      id: e.id,
      entityName,
      amount: Number(e.amount),
      donatedAt: e.donated_at ?? '',
      notes: e.notes ?? '',
    };
  });
}